import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  ElementRef,
  Renderer2,
} from '@angular/core';
import {
  TranslationService,
  Language,
} from '../../appServices/translation.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-language-switcher',
  templateUrl: './language-switcher.component.html',
  styleUrls: ['./language-switcher.component.scss'],
  standalone: false,
})
export class LanguageSwitcherComponent implements OnInit, OnDestroy {
  supportedLanguages: Language[] = [];
  currentLanguage: string = 'en';
  isOpen: boolean = false;
  isDarkMode: boolean = false;
  isLoading: boolean = false;
  loadingLanguage: string = '';

  private subscription: Subscription = new Subscription();
  private themeObserver?: MutationObserver;

  constructor(
    private translationService: TranslationService,
    private elementRef: ElementRef,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.supportedLanguages = this.translationService.getSupportedLanguages();
    this.currentLanguage = this.translationService.getCurrentLanguage();
    this.detectTheme();
    this.setupThemeObserver();

    // Subscribe to language changes
    this.subscription.add(
      this.translationService.currentLanguage$.subscribe((lang) => {
        this.currentLanguage = lang;
        this.isOpen = false; // Close dropdown when language changes
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  }

  /**
   * Get current language info
   */
  get currentLanguageInfo(): Language {
    return (
      this.supportedLanguages.find(
        (lang) => lang.code === this.currentLanguage
      ) || this.supportedLanguages[0]
    );
  }

  /**
   * Toggle dropdown visibility
   */
  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
  }

  /**
   * Select a language and close dropdown with enhanced loading state
   */
  async selectLanguage(languageCode: string): Promise<void> {
    if (this.isLoading || languageCode === this.currentLanguage) {
      return;
    }

    this.isLoading = true;
    this.loadingLanguage = languageCode;

    const startTime = performance.now();

    try {
      // Pre-load the language file to show immediate translations
      await this.translationService.setLanguage(languageCode);

      const endTime = performance.now();
      console.log(
        `✅ Language switched to ${languageCode} in ${(
          endTime - startTime
        ).toFixed(0)}ms`
      );

      this.isOpen = false;
    } catch (error) {
      console.error('Failed to switch language:', error);
      // Keep dropdown open on error
    } finally {
      this.isLoading = false;
      this.loadingLanguage = '';
    }
  }

  /**
   * Close dropdown when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const dropdown = target.closest('.language-switcher');
    if (!dropdown) {
      this.isOpen = false;
    }
  }

  /**
   * Handle keyboard navigation
   */
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.isOpen) {
      if (
        event.key === 'Enter' ||
        event.key === ' ' ||
        event.key === 'ArrowDown'
      ) {
        event.preventDefault();
        this.toggleDropdown();
      }
      return;
    }

    const menuItems =
      this.elementRef.nativeElement.querySelectorAll('.dropdown-item');
    const currentIndex = Array.from(menuItems).findIndex((item) =>
      (item as Element).classList.contains('active')
    );

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.isOpen = false;
        break;
      case 'ArrowDown':
        event.preventDefault();
        const nextIndex = (currentIndex + 1) % menuItems.length;
        (menuItems[nextIndex] as HTMLElement).focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        const prevIndex =
          currentIndex <= 0 ? menuItems.length - 1 : currentIndex - 1;
        (menuItems[prevIndex] as HTMLElement).focus();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (currentIndex >= 0) {
          (menuItems[currentIndex] as HTMLElement).click();
        }
        break;
    }
  }

  /**
   * Detect current theme
   */
  private detectTheme(): void {
    const htmlElement = document.documentElement;
    this.isDarkMode =
      htmlElement.getAttribute('data-theme') === 'dark' ||
      (window.matchMedia &&
        window.matchMedia('(prefers-color-scheme: dark)').matches);

    // Add theme class to component for CSS targeting
    this.renderer.addClass(
      this.elementRef.nativeElement,
      this.isDarkMode ? 'dark-theme' : 'light-theme'
    );
  }

  /**
   * Setup theme observer to watch for theme changes
   */
  private setupThemeObserver(): void {
    // Watch for data-theme attribute changes
    this.themeObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'data-theme'
        ) {
          this.detectTheme();
        }
      });
    });

    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    // Watch for system theme changes
    if (window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      mediaQuery.addEventListener('change', () => {
        if (!document.documentElement.hasAttribute('data-theme')) {
          this.detectTheme();
        }
      });
    }
  }

  /**
   * Get theme-aware CSS classes
   */
  get themeClasses(): string {
    return this.isDarkMode ? 'dark-theme' : 'light-theme';
  }
}
